import React, { useEffect, useState } from 'react';
import { Alert, App, Button, Card, Checkbox, Col, DatePicker, Form, Input, InputNumber, Row, Select, Space, Spin } from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import RecruitPeoplePicker from './shared/RecruitPeoplePicker';
import { fmtDateTime, recruitErrorMessage } from './shared/recruitUi';

const MODES = [
  { value: 'InPerson', label: 'In person' },
  { value: 'Online', label: 'Online' },
  { value: 'Phone', label: 'Phone' },
];
const OPEN_STAGES = 'Applied,Screening,Interview,Offer,TalentPool';

const InterviewFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [appSearch, setAppSearch] = useState('');
  const [conflicts, setConflicts] = useState<Array<{ interview_id: string; message: string }>>([]);
  const presetApp = sp.get('application_id') || undefined;

  const { data: existing, isLoading } = useQuery({
    queryKey: hrmsKeys.interview(id || ''),
    queryFn: () => hrmsService.getInterview(id!),
    enabled: isEdit,
  });
  const { data: apps, isFetching } = useQuery({
    queryKey: hrmsKeys.applications({ picker: appSearch }),
    queryFn: () => hrmsService.pageApplications({ page: 1, limit: 20, q: appSearch || undefined, statuses: OPEN_STAGES }),
    enabled: !isEdit,
  });
  const { data: presetRow } = useQuery({
    queryKey: hrmsKeys.application(presetApp || ''),
    queryFn: () => hrmsService.getApplication(presetApp!),
    enabled: Boolean(presetApp) && !isEdit,
  });
  const { data: templates = [] } = useQuery({ queryKey: hrmsKeys.assessmentTemplates(), queryFn: () => hrmsService.listAssessmentTemplates() });

  useEffect(() => {
    if (existing) {
      form.setFieldsValue({
        application_id: existing.application_id,
        scheduled_at: existing.scheduled_at ? dayjs(existing.scheduled_at) : undefined,
        duration_minutes: existing.duration_minutes || 60,
        mode: existing.mode,
        location_text: existing.location_text,
        meeting_link: existing.meeting_link,
        panel_employee_ids: existing.panel_employee_ids_json || [],
        assessment_template_id: existing.assessment_template_id || undefined,
      });
    } else if (presetApp) {
      form.setFieldsValue({ application_id: presetApp });
    }
  }, [existing, presetApp, form]);

  const scheduledAt = Form.useWatch('scheduled_at', form);
  const duration = Form.useWatch('duration_minutes', form);
  const panel = Form.useWatch('panel_employee_ids', form);
  const applicationId = Form.useWatch('application_id', form);
  const mode = Form.useWatch('mode', form);

  useEffect(() => {
    if (!scheduledAt) {
      setConflicts([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const r = await hrmsService.interviewConflicts({
          scheduled_at: dayjs(scheduledAt).toISOString(),
          duration_minutes: duration,
          panel_employee_ids: panel || [],
          application_id: applicationId,
          exclude_id: id,
        });
        setConflicts(r);
      } catch {
        setConflicts([]);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [scheduledAt, duration, panel, applicationId, id]);

  const appOptions = isEdit
    ? existing
      ? [{ value: existing.application_id, label: `${existing.application_number} — ${existing.candidate_name} (${existing.job_title || ''})` }]
      : []
    : [
        ...(presetRow ? [{ value: presetRow.id, label: `${presetRow.application_number} — ${presetRow.candidate_name} (${presetRow.job_title || ''})` }] : []),
        ...(apps?.rows || [])
          .filter((a: any) => a.id !== presetRow?.id)
          .map((a: any) => ({ value: a.id, label: `${a.application_number} — ${a.candidate_name} (${a.job_title || ''})` })),
      ];

  const save = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      const body = { ...v, scheduled_at: v.scheduled_at ? dayjs(v.scheduled_at).toISOString() : undefined };
      const saved = isEdit ? await hrmsService.updateInterview(id!, body) : await hrmsService.createInterview(body);
      message.success(isEdit ? 'Interview updated' : 'Interview scheduled — candidate and panel notified');
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.interviews() });
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.interview(saved.id) });
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.application(saved.application_id) });
      navigate(`/hr/interviews/${saved.id}`);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(recruitErrorMessage(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  if (isEdit && isLoading) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Interview" />}>
        <Spin />
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel={isEdit ? 'Reschedule / edit interview' : 'Schedule interview'}
          subtitle="The candidate gets a calendar invite; panel members are notified"
          primaryActions={
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>
                {isEdit ? 'Save' : 'Schedule'}
              </Button>
            </Space>
          }
        />
      }
    >
      {conflicts.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16, maxWidth: 860 }}
          message="Schedule clash"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {conflicts.map((c, i) => (
                <li key={i}>
                  {c.message} — <a onClick={() => window.open(`/hr/interviews/${c.interview_id}`, '_blank')}>open</a>
                </li>
              ))}
            </ul>
          }
        />
      )}
      <Form
        form={form}
        layout="vertical"
        initialValues={{ mode: 'InPerson', duration_minutes: 60, notify_candidate: true }}
        style={{ maxWidth: 860 }}
      >
        <Card title="Who" style={{ marginBottom: 16 }}>
          <Form.Item name="application_id" label="Application" rules={[{ required: true, message: 'Select an application' }]}>
            <Select
              showSearch
              disabled={isEdit}
              filterOption={false}
              onSearch={setAppSearch}
              notFoundContent={isFetching ? <Spin size="small" /> : 'No open applications'}
              placeholder="Search candidate, job or application number"
              options={appOptions}
            />
          </Form.Item>
          <Form.Item
            name="panel_employee_ids"
            label="Interview panel"
            rules={[{ required: true, type: 'array', min: 1, message: 'Add at least one interviewer' }]}
          >
            <RecruitPeoplePicker mode="multiple" placeholder="Search employees" />
          </Form.Item>
        </Card>
        <Card title="When and where" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} sm={10}>
              <Form.Item name="scheduled_at" label="Date and time" rules={[{ required: true, message: 'Choose date and time' }]}>
                <DatePicker showTime={{ format: 'HH:mm', minuteStep: 5 }} format="DD MMM YYYY HH:mm" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Item name="duration_minutes" label="Duration (min)">
                <InputNumber min={15} max={480} step={15} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item name="mode" label="Mode">
                <Select options={MODES} />
              </Form.Item>
            </Col>
            {mode === 'Online' ? (
              <Col xs={24}>
                <Form.Item name="meeting_link" label="Meeting link" rules={[{ type: 'url', message: 'Enter a valid URL' }]}>
                  <Input placeholder="https://teams.microsoft.com/..." />
                </Form.Item>
              </Col>
            ) : (
              <Col xs={24}>
                <Form.Item name="location_text" label={mode === 'Phone' ? 'Phone number / notes' : 'Location'}>
                  <Input placeholder={mode === 'Phone' ? 'We will call you on…' : 'Head office, Meeting room 2'} />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Card>
        <Card title="Scorecard">
          <Form.Item name="assessment_template_id" label="Criteria template" extra="Each interviewer rates the candidate against these criteria. Leave empty for the default competency template.">
            <Select
              allowClear
              placeholder="Default competency template"
              options={templates.filter((t: any) => t.is_active !== false).map((t: any) => ({ value: t.id, label: t.template_name }))}
            />
          </Form.Item>
          <Form.Item name="notify_candidate" valuePropName="checked" style={{ marginBottom: 0 }}>
            <Checkbox>{isEdit ? 'Email the candidate if the time changes' : 'Email the candidate a calendar invite'}</Checkbox>
          </Form.Item>
          {isEdit && existing?.scheduled_at && (
            <div style={{ marginTop: 8, color: '#8c8c8c' }}>Currently: {fmtDateTime(existing.scheduled_at)}</div>
          )}
        </Card>
      </Form>
    </WorkspaceLayout>
  );
};

export default InterviewFormPage;
